'use client';

// `useTenant` / `useActiveTenantId` — the tenant-facing selectors over the unified
// SessionProvider.
//
// The former TenantProvider component + AuthProvider were merged into
// `@/components/session/SessionProvider` (BUG-929). This file is kept ONLY so the
// ~90 `import { useTenant } from './TenantProvider'` (and
// `useActiveTenantId`) call sites don't have to change; it now selects the tenant
// slice of the single session context.

import { useMemo } from 'react';
import { Tenant, UserTenant } from '@auctogon/admin-isomorphic/types/user';
import { UNCONFIGURED_CLOSE_STAGE_LABELS, type CloseStageLabelSource } from '@auctogon/core/isomorphic/lifecycle';
import { useSessionContext } from '../session/SessionProvider';

/** The tenant slice exposed by {@link useTenant}. */
export interface TenantContextValue {
    currentTenant: UserTenant | null;
    userTenants: UserTenant[];
    allTenants: Tenant[];
    /** True until user + tenants + active tenant are ALL resolved (BUG-929). */
    loading: boolean;
    setCurrentTenant: (tenant: UserTenant) => void;
    refreshTenants: () => Promise<void>;
}

/**
 * Client-side tenant accessor. Throws outside a SessionProvider (matches the
 * historical `useTenant` contract — callers that must tolerate absence use
 * {@link useActiveTenantId} instead).
 */
export function useTenant(): TenantContextValue {
    const ctx = useSessionContext();
    const value = useMemo<TenantContextValue | null>(() => {
        if (!ctx) return null;
        const { currentTenant, userTenants, allTenants, loading, setCurrentTenant, refreshTenants } = ctx;
        return { currentTenant, userTenants, allTenants, loading, setCurrentTenant, refreshTenants };
    }, [ctx]);
    if (!value) throw new Error('useTenant must be used within a SessionProvider');
    return value;
}

/**
 * The active tenant id, or `null` when there's no SessionProvider ancestor.
 * Unlike {@link useTenant} this does NOT throw — safe for hooks like
 * `usePermissions` that may render outside the provider (static generation,
 * isolated tests). BUG-211 PR-2: `usePermissions` uses this to scope roles to the
 * active tenant without hard-coupling to the provider.
 */
export function useActiveTenantId(): string | null {
    return useSessionContext()?.currentTenant?.id ?? null;
}

/**
 * BL-2282 (PRJ-89) — the active house's Closed label and closing-stage names,
 * the label source every shared unit names a closed auction through (ADR 029:
 * labels are never props). Like {@link useActiveTenantId} it does NOT throw:
 * outside a SessionProvider, or before the bootstrap selects the labels, it
 * answers the unconfigured house (`closedLabel: null` — the default word — and
 * no stages), which is exactly what a house with nothing configured sees.
 *
 * @returns The house's labels, or the unconfigured default.
 */
export function useCloseStageLabels(): CloseStageLabelSource {
    return useSessionContext()?.currentTenant?.closeStages ?? UNCONFIGURED_CLOSE_STAGE_LABELS;
}
